import { FC, useEffect, useState } from 'react';

import { isEmpty } from 'remeda';
import { BaseButton, BaseModal } from 'wbs-components-lib';

import { Box, Typography } from '@mui/material';

import { RewardModelWithChance } from '@shared/types';
import {
  getChangedRewardsInfo,
  getObjectIdConvertedTimestamp,
  transformRewardsFromMapToObject,
  transformRewardsFromObjectToMap,
} from '@shared/utils';

import {
  useBulkDeleteRewardsMutation,
  useCreateRewardMutation,
  useLazyGetRewardsByIdsQuery,
  useUpdateReferralUserRewardChanceMutation,
  useUpdateRewardMutation,
} from '@services';

import { ReferralUserRewardUpdateForm, ReferralUserRewardUpdateFormValues } from '../ReferralUserRewardUpdateForm';

import { ReferralRewardUserUpdateModalProps } from './types';
import { getUserRewardProgressEditFormInitialValues } from './utils';

import styles from './ReferralRewardUserUpdateModal.module.css';

const { userProgressInfoContainer, userProgressInfoTextContainer } = styles;

export const ReferralRewardUserUpdateModal: FC<ReferralRewardUserUpdateModalProps> = ({
  isOpened,
  handleClose,
  referralRewardValue,
  userId,
  personalChance,
  userProgress,
  handleOpenConfirmModal,
  referralSystemStartDate,
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [initialRewards, setInitialRewards] = useState<RewardModelWithChance[]>([]);

  const [updateReferralUserRewardChance] = useUpdateReferralUserRewardChanceMutation();

  const [bulkDeleteRewards] = useBulkDeleteRewardsMutation();
  const [getRewardsByIds] = useLazyGetRewardsByIdsQuery();
  const [createReward] = useCreateRewardMutation();
  const [updateReward] = useUpdateRewardMutation();

  useEffect(() => {
    if (isOpened) {
      const getInitialRewards = async () => {
        if (isEmpty(personalChance)) {
          return;
        }

        const currentRewards = await getRewardsByIds(Object.keys(personalChance)).unwrap();

        setInitialRewards(transformRewardsFromObjectToMap(personalChance, currentRewards));
      };

      getInitialRewards();
    }
  }, [isOpened, personalChance]);

  const handleUpdate = async (formValues: ReferralUserRewardUpdateFormValues) => {
    const { personalChance } = formValues;

    setIsLoading(true);
    try {
      const { deletedRewardIds, createdRewardItems, updatedRewardItems, restRewardItems } = getChangedRewardsInfo(
        initialRewards,
        personalChance
      );

      const isDeletedRewardsExisting = deletedRewardIds.length !== 0;

      if (isDeletedRewardsExisting) {
        await bulkDeleteRewards(deletedRewardIds);
      }

      let createdRewards: RewardModelWithChance[] = [];
      let updatedRewards: RewardModelWithChance[] = [];

      const isCreatedRewardsExisting = createdRewardItems.length !== 0;

      if (isCreatedRewardsExisting) {
        createdRewards = await Promise.all(
          createdRewardItems.map(async (reward) => {
            const { id, type, value } = await createReward(reward).unwrap();

            return new RewardModelWithChance(id, type, value, reward.chance);
          })
        );
      }

      const isUpdatedRewardsExisting = updatedRewardItems.length !== 0;

      if (isUpdatedRewardsExisting) {
        updatedRewards = await Promise.all(
          updatedRewardItems.map(async ({ id, chance, ...rewardParams }) => {
            const { type, value } = await updateReward({
              rewardId: id,
              payload: rewardParams,
            }).unwrap();

            return new RewardModelWithChance(id, type, value, chance);
          })
        );
      }

      await updateReferralUserRewardChance({
        userId,
        payload: {
          personalChance: transformRewardsFromMapToObject([...createdRewards, ...updatedRewards, ...restRewardItems]),
        },
      });

      handleClose();
    } catch (error) {
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  };

  const initialValues = getUserRewardProgressEditFormInitialValues(personalChance, initialRewards);

  const renderProgressInfo = () => {
    if (userProgress !== null) {
      return (
        <>
          <Typography>Received reward: {userProgress.receivedRewardValue}</Typography>
          <Typography>Total spins: {userProgress.totalSpinsCount}</Typography>
          <Typography>Available spins: {userProgress.availableSpinsCount}</Typography>
        </>
      );
    }
  };

  const isMarkButtonDisabled = Boolean(userProgress && referralRewardValue > userProgress.receivedRewardValue);
  const referralsCount =
    userProgress?.referralInviter !== undefined && referralSystemStartDate !== undefined
      ? userProgress.referralInviter.referralIds.filter(
          (referralId) => getObjectIdConvertedTimestamp(referralId) >= new Date(referralSystemStartDate)
        ).length
      : 0;

  return (
    <BaseModal
      headerTitle="Edit User Reward Chance"
      isOpened={isOpened}
      handleClose={handleClose}
      isFooterNeeded={false}
    >
      <Box className={userProgressInfoContainer}>
        <Box className={userProgressInfoTextContainer}>
          <Typography>Referrals count: {referralsCount}</Typography>
          {renderProgressInfo()}
        </Box>
        <BaseButton disabled={isMarkButtonDisabled} onClick={handleOpenConfirmModal}>
          Mark reward as claimed
        </BaseButton>
      </Box>
      <ReferralUserRewardUpdateForm
        submitButtonCaption="Edit"
        initialValues={initialValues}
        isSubmitting={isLoading}
        onSubmit={handleUpdate}
      />
    </BaseModal>
  );
};
